import { useCallback, useEffect, useRef, useState } from 'react';

export type SwipeAction = 'keep' | 'discard';

interface SwipeCardProps {
  onResolve: (action: SwipeAction) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

const COMMIT_RATIO = 0.28; // fraction of card width that counts as a decision
const FLICK_VELOCITY = 0.6; // px per ms

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * The swipe. Right keeps, left discards.
 *
 * The card tracks the finger exactly — no easing on the way out, because
 * anything but 1:1 tracking feels like lag. Release runs a real spring
 * (stiffness/damping integrated per frame) rather than a CSS easing curve, so an
 * interrupted gesture picks up the existing velocity instead of restarting.
 */
export function SwipeCard({ onResolve, children, disabled }: SwipeCardProps): React.JSX.Element {
  const element = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const state = useRef({
    x: 0,
    velocity: 0,
    startX: 0,
    lastX: 0,
    lastT: 0,
    pointerId: -1,
    frame: 0,
    committed: false,
  });

  const paint = useCallback((x: number) => {
    const node = element.current;
    if (!node) return;
    const width = node.offsetWidth || 1;
    const progress = Math.max(-1, Math.min(1, x / (width * COMMIT_RATIO)));
    node.style.transform = `translate3d(${x}px, 0, 0) rotate(${(x / width) * 2.2}deg)`;
    node.style.setProperty('--swipe-progress', String(progress));
  }, []);

  /** Critically-ish damped spring back to centre. */
  const springHome = useCallback(() => {
    const s = state.current;
    cancelAnimationFrame(s.frame);
    let last = performance.now();

    const step = (now: number): void => {
      const dt = Math.min(32, now - last) / 1000;
      last = now;
      const stiffness = 220;
      const damping = 26;
      const accel = -stiffness * s.x - damping * s.velocity;
      s.velocity += accel * dt;
      s.x += s.velocity * dt;

      if (Math.abs(s.x) < 0.4 && Math.abs(s.velocity) < 12) {
        s.x = 0;
        s.velocity = 0;
        paint(0);
        return;
      }
      paint(s.x);
      s.frame = requestAnimationFrame(step);
    };
    s.frame = requestAnimationFrame(step);
  }, [paint]);

  const flingOut = useCallback(
    (action: SwipeAction) => {
      const s = state.current;
      if (s.committed) return;
      s.committed = true;
      cancelAnimationFrame(s.frame);

      const node = element.current;
      if (!node || prefersReducedMotion()) {
        onResolve(action);
        return;
      }

      const target = (action === 'keep' ? 1 : -1) * (node.offsetWidth + 120);
      node.style.transition = 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms linear';
      node.style.transform = `translate3d(${target}px, 0, 0) rotate(${action === 'keep' ? 6 : -6}deg)`;
      node.style.opacity = '0';
      window.setTimeout(() => onResolve(action), 190);
    },
    [onResolve],
  );

  useEffect(() => {
    const s = state.current;
    return () => cancelAnimationFrame(s.frame);
  }, []);

  const onPointerDown = (event: React.PointerEvent): void => {
    if (disabled || event.button !== 0) return;
    const s = state.current;
    cancelAnimationFrame(s.frame);
    s.pointerId = event.pointerId;
    s.startX = event.clientX - s.x;
    s.lastX = event.clientX;
    s.lastT = event.timeStamp;
    s.velocity = 0;
    element.current?.setPointerCapture(event.pointerId);
    if (element.current) element.current.style.transition = '';
    setDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent): void => {
    const s = state.current;
    if (s.pointerId !== event.pointerId) return;

    const dt = event.timeStamp - s.lastT;
    const dx = event.clientX - s.lastX;
    if (dt > 0) {
      // light smoothing keeps a single jittery sample from throwing the fling
      s.velocity = 0.7 * (dx / dt) + 0.3 * s.velocity;
    }
    s.lastX = event.clientX;
    s.lastT = event.timeStamp;
    s.x = event.clientX - s.startX;
    paint(s.x);
  };

  const onPointerUp = (event: React.PointerEvent): void => {
    const s = state.current;
    if (s.pointerId !== event.pointerId) return;
    s.pointerId = -1;
    setDragging(false);

    const width = element.current?.offsetWidth ?? 1;
    const past = Math.abs(s.x) > width * COMMIT_RATIO;
    const flicked = Math.abs(s.velocity) > FLICK_VELOCITY && Math.sign(s.velocity) === Math.sign(s.x);

    if (past || flicked) {
      flingOut(s.x > 0 ? 'keep' : 'discard');
    } else {
      s.velocity *= 1000; // px/ms -> px/s for the integrator
      springHome();
    }
  };

  return (
    <div className={`swipe${dragging ? ' is-dragging' : ''}`}>
      <div className="swipe__rail swipe__rail--keep" aria-hidden="true">
        Keep
      </div>
      <div className="swipe__rail swipe__rail--discard" aria-hidden="true">
        Discard
      </div>
      <div
        ref={element}
        className="swipe__card"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
}
